import React, { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import Button from "../../components/common/Button";
import { useSwipeable } from "react-swipeable";
import { getJoinLink } from "../../backend/landing/joinLink";
import { getSkills } from "../../backend/weekends/skills";
import { sanitizeHTML } from "../../utils/htmlSanitizer";

const ImageCarousel = ({ imageLinkList }: { imageLinkList: string[] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const showControls = imageLinkList.length > 1;
  const isMobile = useMediaQuery({ query: `(max-width: 768px)` });

  const changeImage = (newIndex: number) => {
    if (isSwiping || newIndex === currentIndex) return;
    setIsSwiping(true);
    setCurrentIndex(newIndex);
    setTimeout(() => setIsSwiping(false), 300); // match transition duration
  };

  const prevImage = () => {
    changeImage((currentIndex - 1 + imageLinkList.length) % imageLinkList.length);
  };

  const nextImage = () => {
    changeImage((currentIndex + 1) % imageLinkList.length);
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: nextImage,
    onSwipedRight: prevImage,
    trackMouse: true,
    delta: 30,
  });

  const containerWidth = isMobile ? "100vw" : "calc(var(--main-container-width) * 0.5)";

  return (
    <div
      {...swipeHandlers}
      style={{
        ...styles.carouselContainer,
        position: "relative",
        left: isMobile ? "calc(0px - var(--main-container-margin))" : undefined,
        width: containerWidth,
        overflow: "hidden",
        touchAction: "pan-y",
      }}
    >
      <div
        style={{
          display: "flex",
          width: `${imageLinkList.length * 100}%`,
          transform: `translateX(-${(100 / imageLinkList.length) * currentIndex}%)`,
          transition: "transform 0.3s ease-in-out",
        }}
      >
        {imageLinkList.map((src, index) => (
          <img
            key={index}
            src={src.split("|")[0]}
            style={{
              ...styles.carouselImage,
              width: containerWidth,
              flexShrink: 0,
            }}
            alt={src.split("|").length>0?src.split("|")[1]:"навичка"}
          />
        ))}
      </div>

      {showControls && (
        <>
          {!isMobile && (
            <>
              <button onClick={prevImage} style={{ ...styles.carouselArrow, left: "5px" }}>
                <img
                  src={"/assets/icons/arrow-white.svg"}
                  style={{ width: "2.5rem", height: "2.5rem" }}
                />
              </button>
              <button onClick={nextImage} style={{ ...styles.carouselArrow, right: "5px" }}>
                <img
                  src={"/assets/icons/arrow-white.svg"}
                  style={{ width: "2.5rem", height: "2.5rem", transform: "rotate(180deg)" }}
                />
              </button>
            </>
          )}
          <div style={{ ...styles.carouselLines, gap: isMobile ? "16px" : "32px" }}>
            {imageLinkList.map((_, index) => (
              <div key={index} onClick={() => changeImage(index)} style={styles.carouselLine}>
                <div
                  style={{
                    height: "4px",
                    transition: "background-color 0.3s",
                    backgroundColor: index === currentIndex ? "var(--olive)" : "black",
                  }}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const SkillView = ({
  imageLinkList,
  skillHeading,
  skillText,
  buttonTitle,
  buttonLink,
  maxText,
}: {
  imageLinkList: string[];
  skillHeading: string;
  skillText: string;
  buttonTitle: string;
  buttonLink: string;
  maxText: string;
}) => {
  const isMobile = useMediaQuery({ query: `(max-width: 768px)` });
  return (
    <div style={{ ...styles.skillContainer, marginBottom: isMobile ? "1.5rem" : "5rem" }}>
      <p style={{ ...styles.mainHeading, fontSize: isMobile ? "9.7vw" : "clamp(3rem,4vw,5rem)" }}>
        {skillHeading}
      </p>
      <div style={{ ...styles.mainSkillContainer, flexDirection: isMobile ? "column" : "row" }}>
        {!isMobile && <ImageCarousel imageLinkList={imageLinkList} />}
        <div
          style={{
            ...styles.skillInfoContainer,
            paddingBottom: imageLinkList.length > 1 ? "36px" : "0px",
          }}
        >
          <div
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(skillText) }}
            style={{
              fontSize: isMobile ? "7vw" : "clamp(1.5rem,2vw,2rem)",
              marginBottom: "1rem",
            }}
          />
          {isMobile && <ImageCarousel imageLinkList={imageLinkList} />}
          <div style={{ display: "flex", flexDirection: "row" }}>
            <Button
              text={buttonTitle}
              internal_link={buttonLink[0] != "/" ? undefined : buttonLink}
              external_link={buttonLink[0] == "/" ? undefined : buttonLink}
              textColor="var(--black)"
              hoverTextColor="var(--orange)"
              border="3px solid var(--black)"
              hoverBorder="3px solid var(--black)"
              backgroundColor="var(--orange)"
              hoverBackgroundColor="var(--black)"
              fontSize={isMobile ? "9vw" : "2.5rem"}
              padding={isMobile ? "1rem 0rem" : "1rem 2rem"}
              fontWeight="bold"
              containerProps={isMobile ? { width: "var(--main-container-width)" } : {}}
              isSimple={true}
              blank_text={maxText}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const LandingSkills = ({ weekendId }: {weekendId:number}) => {
  const isMobile = useMediaQuery({ query: `(max-width: 768px)` });
  const skillList = getSkills(weekendId);
  let maxText = "";
  skillList.forEach((item) => {
    if (item.buttonTitle.length > maxText.length) maxText = item.buttonTitle;
  });
  if (skillList.length === 0) return <div/>;
  return (
    <div style={{ ...styles.skillsWrapper }}>
      <div
        style={{
          ...styles.skillsContainer,
          margin: "0rem auto",
          marginTop: isMobile ? "3.5rem" : "5rem",
        }}
      >
        {skillList.map((item) => (
          <SkillView
            imageLinkList={item.photos.split(",")}
            skillHeading={item.name}
            skillText={item.description}
            buttonTitle={item.buttonTitle}
            buttonLink={getJoinLink(weekendId)}
            maxText={maxText}
          />
        ))}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  skillsWrapper: {
    display: "flex",
    width: "100%",
    backgroundColor: "var(--white)",
    overflow: "visible",
  },
  skillsContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    width: "var(--main-container-width)",
    gap: "2.5rem",
  },
  mainHeading: {
    color: "var(--dark-grey)",
    fontWeight: "bold",
  },

  skillContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "2rem",
  },
  mainSkillContainer: {
    display: "flex",
    flexDirection: "row",
    gap: "3rem",
  },
  skillHeadingText: {
    color: "var(--black)",
    fontWeight: "bold",
  },
  carouselContainer: {
    position: "relative",
    width: "calc(var(--main-container-width) * 0.5)",
    flexShrink: 0,
    margin: "auto",
    display: "flex",
    flexDirection: "column",
    // alignItems: "center",
  },

  carouselImage: {
    width: "100%",
    objectFit: "contain",
    display: "block",
  },

  carouselArrow: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    background: "rgba(0,0,0,0)",
    color: "white",
    border: "none",
    borderRadius: "50%",
    padding: "5px 5px",
    cursor: "pointer",
    zIndex: 2,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  carouselLines: {
    display: "flex",
    justifyContent: "center",
    gap: "16px",
    width: "80%",
    alignSelf: "center",
  },

  carouselLine: {
    height: "36px",
    flex: 1,
    transition: "background-color 0.3s",
    cursor: "pointer",
    padding: "16px 0px",
  },

  skillInfoContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: "2rem",
    paddingBottom: "12px",
  },
};

export default LandingSkills;
